import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageContainer } from '../components/layout/PageContainer';
import { Card } from '../components/ui/Card';
import { StatusBadge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { Select } from '../components/ui/Select';
import { getQAQueue, type InternalQAQueueResponse } from '../api/internal-qa';
import { formatRelativeTime } from '../utils/formatters';

const statusFilters = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending-internal-qa', label: 'Pending Review' },
  { value: 'needs-internal-revision', label: 'Needs Revision' },
  { value: 'tech-qa-failed', label: 'Tech QA Failed' },
  { value: 'pending-customer-review', label: 'Customer Review' },
];

export function InternalQAQueuePage() {
  const [queue, setQueue] = useState<InternalQAQueueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchQueue = async () => {
    try {
      const data = await getQAQueue();
      setQueue(data);
      setError(null);
    } catch (err) {
      setError('Failed to load QA queue');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueue();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchQueue, 30000);
    return () => clearInterval(interval);
  }, []);

  const filteredSites = queue?.queue.filter((site) => {
    if (statusFilter === 'all') return true;
    return site.status === statusFilter;
  }) || [];

  if (loading) {
    return (
      <PageContainer title="Internal QA Queue">
        <div className="flex items-center justify-center min-h-[400px]">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Internal QA Queue"
      subtitle={`${filteredSites.length} sites require attention`}
    >
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">{error}</div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex items-center gap-4">
          <div className="w-64">
            <Select
              label="Filter by Status"
              options={statusFilters}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
          </div>
          <div className="flex-1" />
          <p className="text-sm text-gray-500">
            Auto-refreshes every 30 seconds
          </p>
        </div>
      </Card>

      {/* Queue Table */}
      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Business
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tech QA
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSites.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No sites match the current filter
                  </td>
                </tr>
              ) : (
                filteredSites.map((site) => (
                  <tr key={site.website_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">
                          {site.business_name}
                        </p>
                        {site.cloudflare_url && (
                          <a
                            href={site.cloudflare_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary-600 hover:underline"
                          >
                            {site.cloudflare_url}
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={site.status} />
                    </td>
                    <td className="px-6 py-4">
                      {site.tech_qa_score ? (
                        <TechQAScoreMini score={site.tech_qa_score} />
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatRelativeTime(site.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/internal-qa/${site.website_id}`}
                        className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                      >
                        Review →
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </PageContainer>
  );
}

// Mini tech QA score display
function TechQAScoreMini({ score }: { score: { performance: number; accessibility: number; seo: number; bestPractices: number } }) {
  const avg = Math.round(
    (score.performance + score.accessibility + score.seo + score.bestPractices) / 4
  );

  const getColor = (val: number) => {
    if (val >= 90) return 'text-green-600';
    if (val >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <span className={`font-medium ${getColor(avg)}`}>{avg}</span>
  );
}
